import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getOktErtekeles, createOktErtekeles, updateOktErtekeles, getSzak } from "../../apis/SzakokFinder";
// import "../../components/Ujszak.css";
import SideTile from "../SideTile";
// import "./OktErtekeles.css";
import "./SzakjelentesCreate.css";
import "./RadioButton.css";
import RichTextEditor from "../RichTextEditor";
import { useFormSave } from "../../context/FormSaveContext";
import { LanguageProvider, useLanguage } from "../../context/LanguageContext";
import TopAlert from '../../components/TopAlert';
import { OTHV_OPTIONS, HALLGATOI_VISSZAJELZESEK_OPTIONS, KIEMELKEDO_TANTARGYAK_OPTIONS } from './OktErtekelesOptions';
import { OKT_ERTEKELES_TRANSLATIONS } from './OktErtekelesTranslations';

const OktErtekelesContent = ({ szakKod }) => {
    const [isSideTileVisible] = useState(true);
    const { registerSaveHandler, unregisterSaveHandler } = useFormSave();
    const { language, loading } = useLanguage();
    const { szakId } = useParams();
    const navigate = useNavigate();
    const sideTileRef = useRef(null);

    const [oktErtekelesData, setOktErtekelesData] = useState({
        filling_status: 1,
    });

    // State for radio buttons (single values)
    const [othvEredmenyek, setOthvEredmenyek] = useState("");
    const [othvMegjegyzes, setOthvMegjegyzes] = useState("");
    
    const [hallgatoiVisszajelzesek, setHallgatoiVisszajelzesek] = useState([]);
    const [hallgatoiMegjegyzes, setHallgatoiMegjegyzes] = useState("");
    
    const [kiemelkedoTantargyak, setKiemelkedoTantargyak] = useState("");
    const [kiemelkedoMegjegyzes, setKiemelkedoMegjegyzes] = useState("");

    const [isNew, setIsNew] = useState(true);
    const [finalized, setFinalized] = useState(false);
    const [szakStatus, setSzakStatus] = useState(1); // Mely státuszban van a szakjelentés

    // Get translations and options for current language
    const t = loading ? {} : (OKT_ERTEKELES_TRANSLATIONS[language] || OKT_ERTEKELES_TRANSLATIONS.hu);
    const othvOptions = loading ? [] : (OTHV_OPTIONS[language] || OTHV_OPTIONS.hu);
    const hallgatoiVisszajelzesekOptions = loading ? [] : (HALLGATOI_VISSZAJELZESEK_OPTIONS[language] || HALLGATOI_VISSZAJELZESEK_OPTIONS.hu);
    const kiemelkedoTantargyakOptions = loading ? [] : (KIEMELKEDO_TANTARGYAK_OPTIONS[language] || KIEMELKEDO_TANTARGYAK_OPTIONS.hu);

    // Helper function to build JSONB structure
    const buildRadioData = (selectedValue, options) => {
        if (!selectedValue) return null;
        return {
            selected: selectedValue,
            options: options
        };
    };

    // Helper function to build JSONB structure for multi-select checkboxes
    const buildMultiSelectData = (selectedValues, options) => {
        if (!selectedValues || selectedValues.length === 0) return null;
        return {
            selected: selectedValues,
            options: options
        };
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Lekérjük a szak adatait a státusz beállításához
                const szakResponse = await getSzak(szakId);
                if (szakResponse.data && szakResponse.data.data) {
                    setSzakStatus(szakResponse.data.data.status);
                }

                const response = await getOktErtekeles(szakId);
                if (response.data && response.data.data) {
                    const data = response.data.data;
                    setOktErtekelesData(data);
                    setFinalized(data.filling_status === 3);
                    
                    // Load radio button selections from JSONB
                    if (data.othv_eredmenyek) setOthvEredmenyek(data.othv_eredmenyek.selected || "");
                    if (data.othv_megjegyzes) setOthvMegjegyzes(data.othv_megjegyzes || "");
                    
                    if (data.hallgatoi_visszajelzesek) setHallgatoiVisszajelzesek(data.hallgatoi_visszajelzesek.selected || []);
                    if (data.hallgatoi_megjegyzes) setHallgatoiMegjegyzes(data.hallgatoi_megjegyzes || "");
                    
                    if (data.kiemelkedo_tantargyak) setKiemelkedoTantargyak(data.kiemelkedo_tantargyak.selected || "");
                    if (data.kiemelkedo_megjegyzes) setKiemelkedoMegjegyzes(data.kiemelkedo_megjegyzes || "");
                    
                    setIsNew(false);
                } else {
                    // Handle case when no record exists (404)
                    const newData = {
                        filling_status: 1,
                    };
                    await createOktErtekeles(szakId, newData);
                    setOktErtekelesData(newData);
                    window.location.reload();
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };
        fetchData();
    }, [szakId]);

    // Register auto-save handler for token expiry
    useEffect(() => {
        const autoSaveHandler = async () => {
            const updatedData = { 
                ...oktErtekelesData, 
                filling_status: 2,
                othv_eredmenyek: buildRadioData(othvEredmenyek, othvOptions),
                othv_megjegyzes: othvMegjegyzes,
                hallgatoi_visszajelzesek: buildMultiSelectData(hallgatoiVisszajelzesek, hallgatoiVisszajelzesekOptions),
                hallgatoi_megjegyzes: hallgatoiMegjegyzes,
                kiemelkedo_tantargyak: buildRadioData(kiemelkedoTantargyak, kiemelkedoTantargyakOptions),
                kiemelkedo_megjegyzes: kiemelkedoMegjegyzes
            };
            try {
                if (isNew) {
                    await createOktErtekeles(szakId, updatedData);
                } else {
                    await updateOktErtekeles(szakId, updatedData);
                }
            } catch (error) {
                console.error("Error auto-saving data:", error);
            }
        };

        registerSaveHandler(autoSaveHandler);

        return () => {
            unregisterSaveHandler();
        };
    }, [oktErtekelesData, isNew, szakId, registerSaveHandler, unregisterSaveHandler, 
        othvEredmenyek, othvMegjegyzes, hallgatoiVisszajelzesek, hallgatoiMegjegyzes,
        kiemelkedoTantargyak, kiemelkedoMegjegyzes]);

    const handleSubmit = async (e, status) => {
        e.preventDefault();
        
        const updatedData = { 
            ...oktErtekelesData, 
            filling_status: status,
            othv_eredmenyek: buildRadioData(othvEredmenyek, othvOptions),
            othv_megjegyzes: othvMegjegyzes,
            hallgatoi_visszajelzesek: buildMultiSelectData(hallgatoiVisszajelzesek, hallgatoiVisszajelzesekOptions),
            hallgatoi_megjegyzes: hallgatoiMegjegyzes,
            kiemelkedo_tantargyak: buildRadioData(kiemelkedoTantargyak, kiemelkedoTantargyakOptions),
            kiemelkedo_megjegyzes: kiemelkedoMegjegyzes
        };


        try {
            if (isNew) {
                await createOktErtekeles(szakId, updatedData);
            } else {
                await updateOktErtekeles(szakId, updatedData);
            }

            window.location.reload();
        } catch (error) {
            console.error("Hiba az adatok mentésekor:", error);
            alert("Hiba történt az adatok mentésekor.");
        }
    };

    const handleNext = () => {
        navigate(`/szakjelentes_letrehozas/${szakId}/tanulmanyi-korulmenyek`);
    };

    const handleBack = () => {
        navigate(`/szakjelentes_letrehozas/${szakId}/meghivott-eloadok`);
    };

    const isFormValid = () => {
        // Only radio button selections are required, RichTextEditors are optional
        return othvEredmenyek !== "" &&
               hallgatoiVisszajelzesek !== "" &&
               kiemelkedoTantargyak !== "";
    };

    const handleFinalize = async (e) => {
        if (oktErtekelesData.filling_status === 3) {
            handleSubmit(e, 2);
        } else {
            if (isFormValid()) {
                handleSubmit(e, 3);
            } else {
                setAlertMessage("Minden mezőt ki kell tölteni!");
                setShowAlert(true);
            }
        }
    };

    // Top alert state
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div className="szakjelentes-create-wrapper">
        <div className="uj-szak-page">
            {showAlert && (
                <TopAlert message={alertMessage} onClose={() => setShowAlert(false)} duration={5000} />
            )}
            <SideTile ref={sideTileRef} id={szakId} texts={t.sidebar || []}
            isVisible={isSideTileVisible}
            />
            <div className="uj-szak-container"> 
                <h1 className="esg-title">{t.esgTitle}</h1>
                <h1>{t.pageTitle}</h1>
                <p>{t.description} {t.sourceLabel} <a href="https://othv.sze.hu/kezdolap" target="_blank" rel="noopener noreferrer">https://othv.sze.hu/kezdolap</a></p>
                
                <form onSubmit={(e) => handleSubmit(e, 2)}>
                    <div className="card">
                        <h2>{t.section1?.title}</h2>
                        <div className="form-group">
                            {othvOptions.map((option) => (
                                <div className="radio-group" key={option.value}>
                                    <input
                                        type="radio"
                                        id={`othv_${option.value}`}
                                        name="othv_eredmenyek"
                                        value={option.value}
                                        checked={othvEredmenyek === option.value}
                                        onChange={(e) => setOthvEredmenyek(e.target.value)}
                                        disabled={finalized}
                                    />
                                    <label htmlFor={`othv_${option.value}`}>{option.label}</label>
                                </div>
                            ))}
                        </div>

                        <h4>{t.section1?.noteLabel}</h4>
                        <div className="form-group">
                            <RichTextEditor
                                name="othv_megjegyzes"
                                value={othvMegjegyzes}
                                onChange={setOthvMegjegyzes}
                                disabled={finalized}
                            />
                        </div>
                    </div>

                    <div className="card">
                        <h2>{t.section2?.title}</h2>
                        <div className="form-group">
                            {hallgatoiVisszajelzesekOptions.map((option) => (
                                <div className="checkbox-group" key={option.value}>
                                    <input
                                        type="checkbox"
                                        id={`hallgatoi_${option.value}`}
                                        name="hallgatoi_visszajelzesek"
                                        value={option.value}
                                        checked={hallgatoiVisszajelzesek.includes(option.value)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setHallgatoiVisszajelzesek([...hallgatoiVisszajelzesek, option.value]);
                                            } else {
                                                setHallgatoiVisszajelzesek(hallgatoiVisszajelzesek.filter(item => item !== option.value));
                                            }
                                        }}
                                        disabled={finalized}
                                    />
                                    <label htmlFor={`hallgatoi_${option.value}`}>{option.label}</label>
                                </div>
                            ))}
                        </div>

                        <h4>{t.section2?.noteLabel}</h4>
                        <div className="form-group">
                            <RichTextEditor
                                name="hallgatoi_megjegyzes"
                                value={hallgatoiMegjegyzes}
                                onChange={setHallgatoiMegjegyzes}
                                disabled={finalized}
                            />
                        </div>
                    </div>

                    <div className="card">
                        <h2>{t.section3?.title}</h2>
                        <div className="form-group">
                            {kiemelkedoTantargyakOptions.map((option) => (
                                <div className="radio-group" key={option.value}>
                                    <input
                                        type="radio"
                                        id={`kiemelkedo_${option.value}`}
                                        name="kiemelkedo_tantargyak"
                                        value={option.value}
                                        checked={kiemelkedoTantargyak === option.value}
                                        onChange={(e) => setKiemelkedoTantargyak(e.target.value)}
                                        disabled={finalized}
                                    />
                                    <label htmlFor={`kiemelkedo_${option.value}`}>{option.label}</label>
                                </div>
                            ))}
                        </div>

                        <h4>{t.section3?.noteLabel}</h4>
                        <div className="form-group">
                            <RichTextEditor
                                name="kiemelkedo_megjegyzes"
                                value={kiemelkedoMegjegyzes}
                                onChange={setKiemelkedoMegjegyzes}
                                disabled={finalized}
                            />
                        </div>
                    </div>

                    <div className="button-container">
                        <button className="szak-button" type="button" onClick={handleBack}>{t.buttons?.previous}</button>
                        <button className="szak-button" type="button" onClick={handleNext}>{t.buttons?.next}</button>
                    </div>
                    <div className="szak-button-container">
                        <button className="szak-button" type="submit" disabled={finalized || szakStatus >= 2}>{t.buttons?.save}</button>
                        <button className="szak-button finalize-button" type="button" onClick={handleFinalize} disabled={szakStatus >= 2}>
                            {oktErtekelesData.filling_status === 3 ? t.buttons?.unfinalize : t.buttons?.finalize}
                        </button>
                    </div>
                </form>
            </div>
        </div>
        </div>
    );
};

const OktErtekeles = () => {
    const { szakId } = useParams();
    const [szakKod, setSzakKod] = useState(null);

    useEffect(() => {
        const fetchSzakKod = async () => {
            try {
                const response = await getSzak(szakId);
                setSzakKod(response.data.data.szak_kod);
            } catch (error) {
                console.error("Error fetching szak_kod:", error);
            }
        };

        fetchSzakKod();
    }, [szakId]);

    if (!szakKod) {
        return <div>Loading page...</div>;
    }

    return (
        <LanguageProvider szakKod={szakKod}>
            <OktErtekelesContent szakKod={szakKod} />
        </LanguageProvider>
    );
};

export default OktErtekeles;
        